import { defineStore } from "pinia";
import dayjs from "dayjs";
import { DefaultService } from "~/api/njorda";
import type { TSystemStatusEvent as MaintenanceEvent } from "~/interfaces/api";

const SESSION_STORAGE_KEY = "system/maintenance/banners/shown";

export const useMaintenanceStore = defineStore("maintenance", {
  state: () => ({
    events: [] as MaintenanceEvent[], // Use spread to avoid mutation issues if initialEvents is reused
    shownUpcomingEventId: sessionStorage.getItem(SESSION_STORAGE_KEY),
    isLoading: false,
  }),
  getters: {
    upcomingMaintenanceEvent({ events }): MaintenanceEvent | null {
      if (!events || events.length < 1) {
        return null;
      }
      const futureSortedEvents = events
        .filter(
          (event) =>
            event.status === "scheduled" &&
            dayjs(event.planned_start).isAfter(dayjs()) &&
            dayjs(event.planned_start).isBefore(dayjs().add(1, "week")),
        )
        .sort((a, b) => (dayjs(a.planned_start).isAfter(dayjs(b.planned_start)) ? 1 : -1));

      return futureSortedEvents?.[0] ?? null;
    },
    upcomingMaintenanceNotShownEvent(): MaintenanceEvent | null {
      if (!this.upcomingMaintenanceEvent) {
        return null;
      }
      const shownEventId = this.shownUpcomingEventId;
      if (shownEventId && this.upcomingMaintenanceEvent.id === shownEventId) {
        return null;
      }
      return this.upcomingMaintenanceEvent;
    },
    activeMaintenanceEvent(state) {
      if (!state.events || state.events.length < 1) {
        return null;
      }
      return state.events.find((event) => event.status === "ongoing") ?? null;
    },
    // Computed properties like "events" are directly accessible from the store instance
    // so a specific getter for all events isn"t strictly necessary unless filtering/transforming.
  },
  actions: {
    fetchEvents() {
      this.isLoading = true;
      const promise = DefaultService.getB2BV1SystemStatusEvents();
      promise.then((res) => {
        this.events = res?.items ?? [];
        this.isLoading = false;
      });
      return promise;
    },
    setEventShownToday(id: string) {
      this.shownUpcomingEventId = id;
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    },
  },
});
